import React, { useState, useMemo, useEffect } from 'react';
import { Plus, Trash2, CreditCard } from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import Modal from '../../shared/components/Modal';

export default function CarCreditModal({ car, settings, onUpdate, onClose }) {
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  
  const [data, setData] = useState({
    credit: car.credit || false,
    creditTotal: car.creditTotal || 0,
    creditRemaining: car.creditRemaining || 0,
    monthlyPayment: car.monthlyPayment || 0,
    bankInstallments: car.bankInstallments || []
  });
  
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDate, setNewDate] = useState(new Date().toISOString().split('T')[0]);
  const [newAmount, setNewAmount] = useState('');

  // Keep internal state in sync with external car prop updates
  useEffect(() => {
    setData({
      credit: car.credit || false,
      creditTotal: car.creditTotal || 0,
      creditRemaining: car.creditRemaining || 0,
      monthlyPayment: car.monthlyPayment || 0,
      bankInstallments: car.bankInstallments || []
    });
  }, [car]);

  // Calculate stats
  const stats = useMemo(() => {
    const all = data.bankInstallments || [];
    const paid = all.filter(i => i.paid);
    const unpaid = all.filter(i => !i.paid);
    const today = new Date().toISOString().split('T')[0];
    const overdue = unpaid.filter(i => i.dueDate < today);
    
    const totalPaid = paid.reduce((sum, i) => sum + (parseFloat(i.paidAmount || i.amount) || 0), 0);
    const totalUnpaid = unpaid.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);
    
    // Next installment (closest unpaid)
    const sortedUnpaid = [...unpaid].sort((a, b) => (a.dueDate || '').localeCompare(b.dueDate || ''));
    const nextInstallment = sortedUnpaid[0];
    
    return {
      total: all.length,
      paid: paid.length,
      unpaid: unpaid.length,
      overdue: overdue.length,
      totalPaid,
      totalUnpaid,
      nextInstallment
    };
  }, [data.bankInstallments]);

  // Save credit info to DB
  const saveCredit = async () => {
    try {
      await onUpdate(car.id, {
        credit: data.credit,
        creditTotal: parseFloat(data.creditTotal) || 0,
        creditRemaining: parseFloat(data.creditRemaining) || 0,
        monthlyPayment: parseFloat(data.monthlyPayment) || 0,
        bankInstallments: data.bankInstallments
      });
      alert(isAr ? '✅ تم الحفظ' : '✅ Enregistré');
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  // Toggle paid status
  const togglePaid = async (idx) => {
    const updated = [...data.bankInstallments];
    const inst = updated[idx];
    if (inst.paid) {
      inst.paid = false;
      inst.paidDate = null;
      inst.paidAmount = 0;
    } else {
      inst.paid = true;
      inst.paidDate = new Date().toISOString().split('T')[0];
      inst.paidAmount = inst.amount;
    }
    setData(prev => ({ ...prev, bankInstallments: updated }));
    
    // Save immediately
    try {
      await onUpdate(car.id, { bankInstallments: updated });
    } catch (err) {
      console.error(err);
    }
  };

  // Delete installment
  const deleteInst = async (idx) => {
    if (!confirm(isAr ? 'حذف هذا القسط؟' : 'Supprimer cette échéance?')) return;
    const updated = data.bankInstallments.filter((_, i) => i !== idx);
    setData(prev => ({ ...prev, bankInstallments: updated }));
    try {
      await onUpdate(car.id, { bankInstallments: updated });
    } catch (err) {
      console.error(err);
    }
  };

  // Add new installment
  const addInstallment = async () => {
    const amount = parseFloat(newAmount);
    if (!newDate || !amount || amount <= 0) {
      return alert(isAr ? '⚠️ يرجى ملء التاريخ والمبلغ' : '⚠️ Date et montant requis');
    }
    
    const newInst = {
      id: `inst_${Date.now()}`,
      dueDate: newDate,
      amount: amount,
      paid: false,
      paidDate: null,
      paidAmount: 0,
      note: ''
    };
    
    const updated = [...(data.bankInstallments || []), newInst].sort((a, b) => 
      (a.dueDate || '').localeCompare(b.dueDate || '')
    );
    setData(prev => ({ ...prev, bankInstallments: updated }));
    
    try {
      await onUpdate(car.id, { bankInstallments: updated });
      setNewAmount('');
      setShowAddForm(false);
    } catch (err) {
      console.error(err);
    }
  };

  // Auto-generate installments
  const generateInstallments = async () => {
    const total = parseFloat(data.creditTotal) || 0;
    const monthly = parseFloat(data.monthlyPayment) || 0;
    if (!total || !monthly) {
      return alert(isAr 
        ? '⚠️ يرجى تحديد القيمة الإجمالية والقسط الشهري أولاً' 
        : '⚠️ Définir le crédit total et la mensualité d\'abord');
    }
    
    if (data.bankInstallments && data.bankInstallments.length > 0) {
      if (!confirm(isAr 
        ? 'سيتم استبدال الأقساط الحالية. هل أنت متأكد؟' 
        : 'Les échéances actuelles seront remplacées. Continuer?')) return;
    }
    
    const startStr = prompt(
      isAr ? 'تاريخ أول قسط (YYYY-MM-DD):' : 'Date du premier paiement (YYYY-MM-DD):',
      new Date().toISOString().split('T')[0]
    );
    if (!startStr) return;
    
    const nbInstallments = Math.ceil(total / monthly);
    const installments = [];
    const startDate = new Date(startStr);
    
    for (let i = 0; i < nbInstallments; i++) {
      const d = new Date(startDate);
      d.setMonth(d.getMonth() + i);
      installments.push({
        id: `inst_${Date.now()}_${i}`,
        dueDate: d.toISOString().split('T')[0],
        amount: i === nbInstallments - 1 ? (total - monthly * (nbInstallments - 1)) : monthly,
        paid: false,
        paidDate: null,
        paidAmount: 0,
        note: ''
      });
    }
    
    setData(prev => ({ ...prev, bankInstallments: installments }));
    try {
      await onUpdate(car.id, { bankInstallments: installments });
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Modal 
      onClose={onClose} 
      title={`💳 ${isAr ? 'الإئتمان البنكي' : 'Crédit Bancaire'} - ${car.brand} ${car.model}`}
    >
      <div>
        {/* Car info banner */}
        <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🚗</span>
            <div>
              <h3 className="font-bold text-gray-900">{`${car.brand} ${car.model}`}</h3>
              <p className="text-xs text-gray-600 font-mono">{car.plate}</p>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={data.credit}
              onChange={e => setData(prev => ({ ...prev, credit: e.target.checked }))}
              className="w-5 h-5 text-blue-600 rounded cursor-pointer"
            />
            <span className="text-sm font-bold text-gray-800">
              {isAr ? '🏦 يوجد إئتمان' : '🏦 A un crédit'}
            </span>
          </label>
        </div>
        
        {/* Credit form (only if active) */}
        {data.credit && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <h4 className="font-bold text-amber-900 text-sm mb-3">
              💰 {isAr ? 'تفاصيل الإئتمان' : 'Détails du crédit'}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  {isAr ? 'المجموع الكلي' : 'Total crédit'}
                </label>
                <input
                  type="number"
                  value={data.creditTotal}
                  onChange={e => setData(prev => ({ ...prev, creditTotal: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-amber-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  {isAr ? 'المتبقي' : 'Restant'}
                </label>
                <input
                  type="number"
                  value={data.creditRemaining}
                  onChange={e => setData(prev => ({ ...prev, creditRemaining: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-amber-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  {isAr ? 'القسط الشهري' : 'Mensualité'}
                </label>
                <input
                  type="number"
                  value={data.monthlyPayment}
                  onChange={e => setData(prev => ({ ...prev, monthlyPayment: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-amber-500 outline-none"
                />
              </div>
            </div>
            <button
              onClick={saveCredit}
              className="w-full px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-bold transition shadow-sm"
            >
              💾 {isAr ? 'حفظ تفاصيل الإئتمان' : 'Enregistrer les détails'}
            </button>
          </div>
        )}
        
        {/* Stats */}
        {data.credit && (data.bankInstallments?.length > 0) && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg text-center">
              <div className="text-[10px] text-blue-700 font-bold uppercase">{isAr ? 'المجموع' : 'Total'}</div>
              <div className="text-xl font-black text-blue-900">{stats.total}</div>
            </div>
            <div className="p-2 bg-green-50 border border-green-200 rounded-lg text-center">
              <div className="text-[10px] text-green-700 font-bold uppercase">{isAr ? 'مدفوع' : 'Payés'}</div>
              <div className="text-xl font-black text-green-900">{stats.paid}</div>
              <div className="text-[9px] text-green-700 font-mono">{stats.totalPaid.toFixed(0)} {settings.currency}</div>
            </div>
            <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg text-center">
              <div className="text-[10px] text-amber-700 font-bold uppercase">{isAr ? 'متبقي' : 'À payer'}</div>
              <div className="text-xl font-black text-amber-900">{stats.unpaid}</div>
              <div className="text-[9px] text-amber-700 font-mono">{stats.totalUnpaid.toFixed(0)} {settings.currency}</div>
            </div>
            <div className={`p-2 rounded-lg text-center border ${stats.overdue > 0 ? 'bg-red-50 border-red-200 animate-pulse' : 'bg-gray-50 border-gray-200'}`}>
              <div className={`text-[10px] font-bold uppercase ${stats.overdue > 0 ? 'text-red-700' : 'text-gray-500'}`}>
                {isAr ? 'متأخر' : 'En retard'}
              </div>
              <div className={`text-xl font-black ${stats.overdue > 0 ? 'text-red-900' : 'text-gray-400'}`}>{stats.overdue}</div>
            </div>
          </div>
        )}
        
        {/* Next installment alert */}
        {data.credit && stats.nextInstallment && (() => {
          const today = new Date().toISOString().split('T')[0];
          const daysLeft = Math.ceil((new Date(stats.nextInstallment.dueDate) - new Date(today)) / 86400000);
          const isOverdue = daysLeft < 0;
          const urgent = daysLeft <= 3 && daysLeft >= 0;
          const soon = daysLeft <= 15 && daysLeft > 3;
          
          return (
            <div className={`mb-4 p-3 rounded-xl border-2 ${
              isOverdue ? 'bg-red-50 border-red-300 animate-pulse' :
              urgent ? 'bg-orange-50 border-orange-300' :
              soon ? 'bg-yellow-50 border-yellow-300' :
              'bg-blue-50 border-blue-300'
            }`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-xs font-bold uppercase mb-1">
                    {isOverdue ? (isAr ? '⚠️ قسط متأخر!' : '⚠️ Échéance en retard!') :
                     urgent ? (isAr ? '🔥 قسط قريب جداً!' : '🔥 Échéance proche!') :
                     (isAr ? '📅 القسط القادم' : '📅 Prochaine échéance')}
                  </div>
                  <div className="font-mono text-sm font-bold">📅 {stats.nextInstallment.dueDate}</div>
                  <div className="text-xs mt-1">
                    {isOverdue 
                      ? (isAr ? `متأخر بـ ${Math.abs(daysLeft)} يوم` : `En retard de ${Math.abs(daysLeft)} jours`)
                      : daysLeft === 0
                        ? (isAr ? '🎯 اليوم!' : '🎯 Aujourd\'hui!')
                        : (isAr ? `بعد ${daysLeft} يوم` : `Dans ${daysLeft} jours`)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-black text-gray-900">
                    {`${stats.nextInstallment.amount} ${settings.currency}`}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
        
        {/* Actions */}
        {data.credit && (
          <div className="flex gap-2 mb-4 flex-wrap">
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="flex-1 min-w-[140px] px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-bold flex items-center justify-center gap-1.5 transition"
            >
              <Plus className="w-4 h-4" /> {isAr ? 'إضافة قسط' : 'Ajouter échéance'}
            </button>
            <button
              onClick={generateInstallments}
              className="flex-1 min-w-[140px] px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-bold flex items-center justify-center gap-1.5 transition"
            >
              ⚡ {isAr ? 'توليد تلقائي' : 'Générer auto'}
            </button>
          </div>
        )}
        
        {/* Add new installment form */}
        {data.credit && showAddForm && (
          <div className="mb-4 p-3 bg-blue-50 border-2 border-blue-300 rounded-xl">
            <h4 className="font-bold text-blue-900 text-sm mb-2">
              ➕ {isAr ? 'قسط جديد' : 'Nouvelle échéance'}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  {isAr ? 'التاريخ' : 'Date'}
                </label>
                <input
                  type="date"
                  value={newDate}
                  onChange={e => setNewDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  {isAr ? 'المبلغ' : 'Montant'}
                </label>
                <input
                  type="number"
                  value={newAmount}
                  onChange={e => setNewAmount(e.target.value)}
                  placeholder={String(data.monthlyPayment || '')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => { setShowAddForm(false); setNewAmount(''); }}
                className="flex-1 px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-bold transition"
              >
                {isAr ? 'إلغاء' : 'Annuler'}
              </button>
              <button
                onClick={addInstallment}
                className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-bold transition"
              >
                ✅ {isAr ? 'إضافة' : 'Ajouter'}
              </button>
            </div>
          </div>
        )}
        
        {/* Installments list */}
        {data.credit && (
          <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="bg-gray-100 px-3 py-2 font-bold text-sm text-gray-750 border-b border-gray-200">
              📋 {isAr ? 'الأقساط' : 'Échéances'} {` (${data.bankInstallments?.length || 0})`}
            </div>
            {(data.bankInstallments?.length || 0) === 0 ? (
              <div className="p-6 text-center text-gray-500 text-sm bg-white">
                <div className="text-3xl mb-2">💳</div>
                <p>{isAr ? 'لا توجد أقساط بعد' : 'Aucune échéance enregistrée'}</p>
                <p className="text-xs text-gray-400 mt-1">{isAr ? 'أضف قسط أو ولّد تلقائياً' : 'Ajoutez ou générez automatiquement'}</p>
              </div>
            ) : (
              <div className="max-h-80 overflow-y-auto divide-y divide-gray-100 bg-white">
                {data.bankInstallments.map((inst, idx) => {
                  const today = new Date().toISOString().split('T')[0];
                  const daysLeft = Math.ceil((new Date(inst.dueDate) - new Date(today)) / 86400000);
                  const isOverdue = !inst.paid && daysLeft < 0;
                  
                  return (
                    <div
                      key={inst.id || idx}
                      className={`flex items-center gap-3 px-3 py-2.5 transition-colors ${
                        inst.paid ? 'bg-green-50/50' : isOverdue ? 'bg-red-50/50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={!!inst.paid}
                        onChange={() => togglePaid(idx)}
                        className="w-5 h-5 text-green-600 rounded cursor-pointer flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`font-mono text-sm font-bold ${inst.paid ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                            📅 {inst.dueDate}
                          </span>
                          {isOverdue && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-red-200 text-red-950 rounded font-bold">
                              {isAr ? 'متأخر' : 'EN RETARD'}
                            </span>
                          )}
                          {inst.paid && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-green-200 text-green-950 rounded font-bold">
                              {isAr ? '✅ مدفوع' : '✅ PAYÉ'}
                            </span>
                          )}
                        </div>
                        {inst.paid && inst.paidDate && (
                          <div className="text-[10px] text-green-700 mt-0.5">
                            {isAr ? `دُفع في: ${inst.paidDate}` : `Payé le: ${inst.paidDate}`}
                          </div>
                        )}
                      </div>
                      <div className={`font-mono font-bold text-sm flex-shrink-0 ${inst.paid ? 'text-green-700' : 'text-gray-900'}`}>
                        {`${inst.amount} ${settings.currency}`}
                      </div>
                      <button
                        onClick={() => deleteInst(idx)}
                        className="text-red-500 hover:bg-red-100 p-1.5 rounded transition flex-shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        
        {/* Close button */}
        <div className="mt-4 pt-3 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-bold text-sm transition"
          >
            {isAr ? 'إغلاق' : 'Fermer'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
